import { withHTMLComponent } from '@updater-legacy/components/custom-element';
import createRouter from '@updater/app-shell';
import gql from 'graphql-tag';
import {
  getRoutes,
  CONTENT_SLOT,
  HEADER_SLOT,
  isTerminalContent,
} from './route-config';
import createClient from './store';
import '@updater-components/step-banner';
import '@updater-components/icon';
import '@updater-components/loading-spinner';
import { PAGE } from '@updater-core/lib/tracker';
import EIFragment from './store/fragments/entity-interface';
import promotionFragment from './store/fragments/promotion';
import claimedPromotionFragment from './store/fragments/claimed-promotion';
import styles from './index.css';
import { STATES } from '../../../../constants';
import { actions, selectors } from '../../../../state';
import { TRACKER_KEY, BASE_PATH } from './constants';
import { provide } from './provide';

// TODO: Workaround for Analytics. We need to allow for DOM events
// to reach the analytics event handlers before navigating.
// https://codepen.io/stiggler/pen/PRggZq
const withTimeout = (fn) => (...args) =>
  new Promise((resolve) => setTimeout(() => resolve(fn.apply(fn, args))));

const html = `
  <div id="${HEADER_SLOT}"></div>
  <div id="${CONTENT_SLOT}">
    <upd-loading-spinner
      style="font-size: 4em; margin: 1.8em auto 1.65em auto; display: block; width: 1em;"
    >
    </upd-loading-spinner>
  </div>
`;

let hasBeenLoadedBefore = false;
class OffersPage extends withHTMLComponent(styles, html)() {
  static props = ['value', 'updStore'];

  updated({
    value: {
      sessionHeaders,
      env,
      moveId,
      user,
      tracker,
      optimizely,
      nextStepCards,
      eligiblePromotions,
      claimedPromotions,
      showSplashPage = false,
      showSplashPageVariation = false,
      dispatch,
    },
    updStore,
  }) {
    this.updStore = updStore;
    this.updState = updStore.getState();
    this.getRouter = () => this.router;
    this.tracker = tracker;
    provide(TRACKER_KEY, tracker);

    const routes = getRoutes({ tracker });

    const getStore = createClient(
      {
        sessionHeaders,
        env,
      },
      {
        eligiblePromotions,
        claimedPromotions,
        moveId,
        hasBeenLoadedBefore,
        user: {
          __typename: 'ClientUser',
          email: user.email,
          phone: user.phone,
        },
        nextStepCards: JSON.stringify(nextStepCards),
      },
      this.getRouter
    );

    getStore.then((store) => {
      this.store = store;
      this.router = createRouter(
        {
          basePath: BASE_PATH,
          dynamicComponentImport: (path) =>
            import(
              /* webpackChunkName: "[request]" */ `OFFERS-REPLACE-ME/${path}.js`
            ),
          element: this.shadowRoot,
          routes,
        },
        {
          store: this.store,
          tracker,
          optimizely,
          showSplashPage,
          showSplashPageVariation,
          dispatch,
        }
      );

      this.router.goToError = () => {
        updStore.dispatch(actions.router.stateGo(STATES.ERROR));
      };
      this.router.goToDashboard = () => {
        updStore.dispatch(actions.router.stateGo(STATES.DASHBOARD));
      };
      this.router.goToNextSteps = () => {
        /* set a timeout to allow track to bubble */
        setTimeout(() => {
          updStore.dispatch(
            actions.step.goToNext(selectors.step.nextStep(updStore.getState()))
          );
        });
      };

      this.router.goTo = withTimeout(this.router.goTo);
      const { goTo } = this.router;
      this.router.goTo = (path) => {
        this.router.desiredPath = path;
        goTo(path);
      };

      const seedIfNotOnLandingPage = window.location.href.match(
        `${BASE_PATH}/$|${BASE_PATH}/deals.*`
      )
        ? Promise.resolve()
        : this.store.query({
            fetchPolicy: 'network-only',
            query: gql`
          query seedclient {
            eligiblePromotions {
              eligiblePromotions {
                promotion {
                  ...${promotionFragment.name}
                }
                validFor {
                  ...${EIFragment.name}
                }
              }
            }

            claimedPromotions {
              claimedPromotions {
                ...${claimedPromotionFragment.name}
              }
            }
          }

          ${promotionFragment.fragment}
          ${claimedPromotionFragment.fragment}
          ${EIFragment.fragment}
        `,
          });

      return seedIfNotOnLandingPage.then(() => this.router.init());
    });

    this.addEventListener('routechange', this.onRouteChange);
    this.addEventListener('routechange', this.handleNextStep(dispatch));
    this.addEventListener('upd-go-to', this.goTo);
  }

  scrollToTop = () => {
    if (window) {
      window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  goTo = ({ detail: { updState } }) =>
    this.updStore.dispatch(actions.router.stateGo(updState));

  onRouteChange = ({
    detail: {
      config: { data: { analytics } = {} },
    },
  }) => {
    this.scrollToTop();

    this.router.lastPathGoneTo = this.router.currentPath;

    const { event, properties = () => {} } = analytics;
    Promise.resolve(properties(this._store)).then((properties) => {
      const srcParam = selectors.router.currentParamsProp('src')(this.updState);
      if (srcParam === 'webpush') {
        this.tracker.track({
          verb: PAGE,
          object: event,
          details: {
            ...properties,
            source: 'webpush',
          },
        });
      } else {
        this.tracker.track({
          verb: PAGE,
          object: event,
          details: properties,
        });
      }
    });
  };

  handleNextStep = (dispatch) => ({
    detail: {
      config: { components },
    },
  }) => {
    if (!components) return;
    const content = components[`#${CONTENT_SLOT}`];
    dispatch(actions.step.isTerminalRoute(isTerminalContent(content)));
  };

  disconnected() {
    hasBeenLoadedBefore = true;

    if (this.router) {
      this.removeEventListener('routechange', this.onRouteChange);
      this.removeEventListener('routechange', this.handleNextStep);
      this.router.destroy();
    }
    if (this.store) {
      this.store.resetStore();
    }
    this.removeEventListener('upd-go-to', this.goTo);
  }
}

customElements.define('upd-promotions', OffersPage);
